<template>
  <q-layout view="lHh Lpr lFf">
    <q-header class="bg-white">
      <header-layout @toggle-drawer="toggleDrawer" />
    </q-header>

    <category-drawer v-model="drawer" />

    <q-footer class="bg-white" bordered>
      <footer-layout />
    </q-footer>

    <q-page-container style="padding: 0">
      <div
        class="row no-wrap ellipsis items-center justify-center q-col-gutter-md q-pa-sm"
      >
        <div>
          <q-input
            v-model="searchBase"
            :placeholder="t('page.job.search_by_location')"
            outlined
            dense
            readonly
            tabindex="-1"
            class="fixed-input-height"
            @click="dialog = !dialog"
          >
            <template #prepend>
              <q-icon :name="mdiMagnify" class="text-primary" />
            </template>
          </q-input>
        </div>
        <q-dialog v-model="dialog" class="dialog-top">
          <q-card style="min-width: 90vw; max-width: 100vw">
            <div class="row justify-end">
              <q-btn
                flat
                round
                :icon="mdiClose"
                class="q-mr-sm"
                color="primary"
                @click="dialog = false"
              />
            </div>
            <q-card-section class="q-pt-none">
              <div>
                <q-input
                  outlined
                  dense
                  v-model="stateSearch"
                  label="State"
                  @update:model-value="onStateInput"
                  @focus="menuState = true"
                  ref="stateInput"
                  :loading="loadingStates"
                >
                  <template #append>
                    <q-icon
                      :name="mdiChevronDown"
                      class="cursor-pointer"
                      @click.stop="menuState = !menuState"
                    />
                  </template>
                </q-input>
                <q-card v-if="menuState" class="menu-dropdown">
                  <q-list bordered separator>
                    <q-item
                      v-for="state in filteredStates"
                      :key="state.id"
                      clickable
                      @click="selectState(state)"
                    >
                      <q-item-section>{{ state.name }}</q-item-section>
                    </q-item>
                  </q-list>
                </q-card>
              </div>

              <div class="row q-col-gutter-sm q-mt-sm">
                <div class="col-8">
                  <q-input
                    outlined
                    v-model="citySearch"
                    label="City"
                    @update:model-value="onCityInput"
                    :disable="!selectedState"
                    dense
                    ref="cityInput"
                    @click.stop="toggleCityMenu"
                    :loading="loadingCities"
                  >
                    <template #append>
                      <q-icon
                        :name="mdiChevronDown"
                        @click.stop="toggleCityMenu"
                        class="cursor-pointer"
                      />
                    </template>
                  </q-input>
                  <div v-if="menuCity">
                    <q-card class="menu-dropdown">
                      <q-list bordered separator>
                        <q-item
                          v-for="city in filteredCities"
                          :key="city.id"
                          clickable
                          @click="selectCity(city)"
                        >
                          <q-item-section>{{ city.name }}</q-item-section>
                        </q-item>
                      </q-list>
                    </q-card>
                  </div>
                </div>

                <div class="col-4">
                  <q-input outlined v-model="zipcode" label="Zipcode" dense />
                </div>
              </div>
            </q-card-section>
            <div class="row q-px-md q-py-sm justify-between">
              <q-btn
                label="Reset"
                outline
                color="dark"
                class="col-5"
                @click="resetSearchAndRoute"
                no-caps
              />
              <q-btn
                label="Search"
                color="primary"
                class="col-5"
                @click="searchTechnician"
                no-caps
              />
            </div>
          </q-card>
        </q-dialog>

        <div
          @click="router.push({ name: POST_SAVED_ROUTE_NAME.ALL_POST_SAVED })"
        >
          <q-btn
            color="primary"
            size="md"
            dense
            no-caps
            :label="t('page.job.post_saved')"
          />
        </div>
        <div @click="router.push({ name: POST_ROUTE_NAME.POST })">
          <q-btn
            outline
            dense
            color="primary"
            size="md"
            style="border-radius: 6px"
            no-caps
          >
            <template #default>
              <div class="row items-center no-wrap">
                <q-icon :name="mdiPlusCircleOutline" />
                <span class="q-ml-sm text-body1">{{ t("page.job.post") }}</span>
              </div>
            </template>
          </q-btn>
        </div>
      </div>
      <separator />
      <default-card :card-items="cardItems" />
      <separator />

      <router-view />
    </q-page-container>
  </q-layout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import HeaderLayout from "@/components/HeaderLayout.vue";
import FooterLayout from "@/components/FooterLayout.vue";
import CategoryDrawer from "@/components/CategoryDrawer.vue";
import Separator from "@/components/Separator.vue";
import DefaultCard from "@/components/card/Default.vue";
import { useAppStore } from "@/stores/appStore";
import { useLocationStore } from "@/stores/locationStore";
import { useLocationSearch } from "@/composables/useLocation";

import nailtech from "@/assets/nailtech.svg";
import salon from "@/assets/salon.svg";
import course from "@/assets/course.svg";
import nailjob from "@/assets/nailjob.svg";
import type { CardItem } from "@/types/common";
import { useLang } from "@/composables/useLang";
import { useBase } from "@/composables/useBase";
import {
  POST_ROUTE_NAME,
  JOB_ROUTE_NAME,
  WORKER_ROUTE_NAME,
  SALON_ROUTE_NAME,
  COURSE_ROUTE_NAME,
  POST_SAVED_ROUTE_NAME
} from "@/libs/route-name";
import {
  mdiMagnify,
  mdiClose,
  mdiChevronDown,
  mdiPlusCircleOutline
} from "@quasar/extras/mdi-v7";

const { t } = useLang();
const { router } = useBase();
const route = useRoute();
const appStore = useAppStore();
const locationStore = useLocationStore();

// Use location search composable
const {
  stateSearch,
  citySearch,
  zipcode,
  selectedState,
  menuState,
  menuCity,
  filteredStates,
  filteredCities,
  loadingStates,
  loadingCities,
  onStateInput,
  onCityInput,
  toggleCityMenu,
  selectState,
  selectCity,
  resetSearch,
  initializeFromRoute
} = useLocationSearch();

const drawer = computed({
  get: () => appStore.drawerOpen,
  set: (value: boolean) => appStore.setDrawerOpen(value)
});

const toggleDrawer = () => {
  appStore.setDrawerOpen(!appStore.drawerOpen);
};

const searchBase = computed(() => {
  const hasFilters =
    route.query.state || route.query.city || route.query.zipcode;
  if (!hasFilters) {
    return t("page.job.search_by_location");
  }
  const filters = [];
  if (route.query.state) filters.push(route.query.state as string);
  if (route.query.city) filters.push(route.query.city as string);
  if (route.query.zipcode) filters.push(route.query.zipcode as string);
  return filters.join(", ");
});

// Watch route changes to sync local state
watch(
  () => route.query,
  newQuery => {
    if (!newQuery.state) {
      resetSearch();
    }
  },
  { immediate: true }
);

const dialog = ref(false);

const cardItems = computed<CardItem[]>(() => [
  {
    name: JOB_ROUTE_NAME.HOME,
    img: nailjob,
    title: t("app.nail_jobs")
  },
  {
    name: WORKER_ROUTE_NAME.HOME,
    img: nailtech,
    title: t("app.nail_techs")
  },
  {
    name: SALON_ROUTE_NAME.HOME,
    img: salon,
    title: t("app.salons_for_sale")
  },
  {
    name: COURSE_ROUTE_NAME.HOME,
    img: course,
    title: t("app.courses")
  }
]);

onMounted(async () => {
  await locationStore.loadStates();
  initializeFromRoute(route.query);
});

const stateInput = ref();
const cityInput = ref();

const updateRouteQuery = () => {
  const currentQuery = { ...route.query };
  currentQuery.state = stateSearch.value;
  currentQuery.city = citySearch.value;
  currentQuery.zipcode = zipcode.value;
  Object.keys(currentQuery).forEach(key => {
    if (!currentQuery[key]) delete currentQuery[key];
  });
  router.replace({ query: currentQuery });
};

const resetSearchAndRoute = () => {
  resetSearch();
  router.replace({ query: {} });
};

const searchTechnician = async () => {
  updateRouteQuery();
  dialog.value = false;
};
</script>

<style scoped>
.fixed-input-height :deep(.q-field__control) {
  height: 30px;
  align-items: center;
  border-radius: 5px !important;
  border: 1px solid var(--q-primary);
}

.fixed-input-height :deep(.q-field__control) {
  font-size: 12px;
}

.fixed-input-height.q-field--focused :deep(.q-field__control) {
  border: none !important;
}

.dialog-top .q-dialog__inner {
  align-items: flex-start !important;
}

.menu-dropdown {
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
}
</style>
